import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';
import { useSelector } from 'react-redux';

import { Flex } from '@atlaskit/primitives';
import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';

import { getPullRequestMergeChecks } from 'src/redux/pull-request/selectors';
import { MergeCheck } from 'src/types';
import globalUrls from 'src/urls/global';

import { useMergeData } from '../../../hooks/merge-data';
import {
  BasicMergeChecksList,
  ForgeMergeCheckResult,
} from '../../merge-checks-card';
import { usePollingResults } from '../../merge-checks-card/hooks';
import {
  classicCheckAllowsMerge,
  customCheckResultAllowsMerge,
  isClassicCheckRecommendedAndFailed,
  isCustomCheckRecommendedAndFailed,
} from '../../merge-checks-card/utils';
import mergeDialogMessages from '../merge-dialog.i18n';

import { messages } from './i18n';

export const MergeWarningSectionMessage = () => {
  const { formatMessage } = useIntl();
  const { canCreatePendingMerge, countFailedChecks } = useMergeData();
  const { data: customMergeChecksData } = usePollingResults();
  const classicMergeChecks = useSelector(getPullRequestMergeChecks);

  if (!countFailedChecks) {
    return null;
  }

  const actions = [
    <SectionMessageAction key="learn-more-link">
      <a
        href={globalUrls.external.suggestOrRequireChecksBeforeMerge}
        target="_blank"
        data-testid="learn-more-link"
      >
        {formatMessage(mergeDialogMessages.mergeWarningLinkText)}
      </a>
    </SectionMessageAction>,
  ];

  if (canCreatePendingMerge) {
    return (
      <SectionMessage
        actions={actions}
        appearance="discovery"
        title={
          countFailedChecks === 1
            ? formatMessage(messages.pendingMergeWarningTitle)
            : formatMessage(messages.mergeWarningTitle, {
                countFailedChecks,
              })
        }
      >
        <FormattedMessage
          {...messages.pendingMergeWarningText}
          values={{
            merge: <strong>Merge</strong>,
          }}
        />
      </SectionMessage>
    );
  }

  const { failedRequiredCustomChecks, failedRecommendedCustomChecks } = (
    customMergeChecksData?.results ?? []
  ).reduce(
    (acc, check) => {
      if (check.checkType === 'pre-merge') {
        if (!customCheckResultAllowsMerge(check)) {
          acc.failedRequiredCustomChecks.push(check);
        } else if (isCustomCheckRecommendedAndFailed(check)) {
          acc.failedRecommendedCustomChecks.push(check);
        }
      }
      return acc;
    },
    {
      failedRequiredCustomChecks: [] as ForgeMergeCheckResult[],
      failedRecommendedCustomChecks: [] as ForgeMergeCheckResult[],
    }
  );

  const { failedRequiredClassicChecks, failedRecommendedClassicChecks } =
    classicMergeChecks.reduce(
      (acc, check) => {
        if (!classicCheckAllowsMerge(check)) {
          acc.failedRequiredClassicChecks.push(check);
        } else if (isClassicCheckRecommendedAndFailed(check)) {
          acc.failedRecommendedClassicChecks.push(check);
        }
        return acc;
      },
      {
        failedRequiredClassicChecks: [] as MergeCheck[],
        failedRecommendedClassicChecks: [] as MergeCheck[],
      }
    );

  const totalFailedRequiredChecks =
    failedRequiredCustomChecks.length + failedRequiredClassicChecks.length;
  const totalFailedRecommendedChecks =
    failedRecommendedCustomChecks.length +
    failedRecommendedClassicChecks.length;

  const hasFailedRequiredChecks = totalFailedRequiredChecks > 0;
  const hasFailedRecommendedChecks = totalFailedRecommendedChecks > 0;

  if (!hasFailedRequiredChecks && !hasFailedRecommendedChecks) {
    return null;
  }

  // Prioritise showing failed required checks over failed recommended checks and we render
  // the section message differently for failed required vs recommended checks.
  const totalFailedChecksToShow = hasFailedRequiredChecks
    ? totalFailedRequiredChecks
    : totalFailedRecommendedChecks;

  return (
    <SectionMessage
      appearance={hasFailedRequiredChecks ? 'warning' : 'information'}
      actions={actions}
      title={
        hasFailedRequiredChecks
          ? formatMessage(messages.unableToMergeTitle)
          : undefined
      }
    >
      <Flex direction="column" gap="space.050">
        <div>
          {formatMessage(messages.failedPreMergeChecksDescription, {
            checkType: (
              <strong>
                {formatMessage(
                  hasFailedRequiredChecks
                    ? messages.required
                    : messages.recommended
                )}
              </strong>
            ),
            total: totalFailedChecksToShow,
          })}
        </div>
        <BasicMergeChecksList
          customMergeCheckResults={
            hasFailedRequiredChecks
              ? failedRequiredCustomChecks
              : failedRecommendedCustomChecks
          }
          classicMergeCheckResults={
            hasFailedRequiredChecks
              ? failedRequiredClassicChecks
              : failedRecommendedClassicChecks
          }
        />
        {hasFailedRequiredChecks && (
          <div>
            {formatMessage(messages.reviewFailedPreMergeChecksDescription, {
              total: totalFailedChecksToShow,
            })}
          </div>
        )}
      </Flex>
    </SectionMessage>
  );
};
